import React, { useState, useEffect } from 'react';
import { 
  Sparkles, 
  Search, 
  FileText, 
  Send, 
  Printer, 
  Copy, 
  Check, 
  ExternalLink, 
  ArrowRight, 
  CheckCircle2, 
  Play, 
  RefreshCw, 
  HelpCircle,
  Briefcase,
  Building,
  PlusCircle,
  Bot,
  Flame,
  Clock,
  Zap,
  ShieldCheck,
  Upload,
  Lock
} from 'lucide-react';
import { MasterProfile, TailorResult, JobApplication } from '../types';
import { tailorResumeWithAI } from '../services/aiService';
import { storageService } from '../services/storageService';

interface SimpleWizardProps {
  masterProfile: MasterProfile;
  setMasterProfile: (p: MasterProfile) => void;
  onSaveToPipeline: (app: JobApplication) => void;
  onOpenPrintPreview: (tailoredSummary?: string, tailoredBullets?: string[]) => void;
  onOpenUploadCvModal: () => void;
}

export const SimpleWizard: React.FC<SimpleWizardProps> = ({
  masterProfile,
  setMasterProfile,
  onSaveToPipeline,
  onOpenPrintPreview,
  onOpenUploadCvModal
}) => {
  // Current active step in the 3-step wizard
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3>(1);

  // Auto-Scraped Job List State
  const [autoJobs, setAutoJobs] = useState<any[]>([]);
  const [isAutoSearching, setIsAutoSearching] = useState(false);
  const [selectedAutoJobId, setSelectedAutoJobId] = useState<string | null>(null);

  // Default Cyber / MSP target role
  const [targetRole, setTargetRole] = useState('Contract Information Security & SOC Analyst');
  const [targetCompany, setTargetCompany] = useState('Vanguard Cyber Defense UK');
  const [jobText, setJobText] = useState(`Urgent contract requirement for an InfoSec & SOC Analyst (Outside IR35).
- Security monitoring, SIEM log analysis (Sentinel / Splunk)
- Vulnerability assessments & remediation (Qualys / Nessus)
- Microsoft 365 Entra ID hardening and EDR incident triage
- £450 - £500/day, immediate remote UK start.`);
  
  // Results
  const [isProcessing, setIsProcessing] = useState(false);
  const [tailorResult, setTailorResult] = useState<TailorResult | null>(null);
  const [copiedSection, setCopiedSection] = useState<string | null>(null);
  const [isSaved, setIsSaved] = useState(false);

  // Auto-fetch fresh jobs on component mount
  const handleAutoSearchJobs = async () => {
    setIsAutoSearching(true);
    try {
      const res = await fetch('/api/fetch-jobs');
      if (res.ok) {
        const data = await res.json();
        if (data.jobs && Array.isArray(data.jobs)) {
          setAutoJobs(data.jobs);
        }
      }
    } catch (err) {
      console.warn("Using default cyber feed...", err);
    }
    setIsAutoSearching(false);
  };

  useEffect(() => {
    handleAutoSearchJobs();
  }, []);

  const handleSelectDiscoveredJob = (job: any) => {
    setSelectedAutoJobId(job.id);
    setTargetRole(job.role);
    setTargetCompany(job.company);
    setJobText(job.description || `${job.role} at ${job.company}. Remote contract UK.`);
  };

  const handleCopy = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopiedSection(key);
    setTimeout(() => setCopiedSection(null), 2500);
  };

  const handleGenerateCV = async () => {
    if (!jobText.trim()) return;
    setIsProcessing(true);
    setIsSaved(false);

    const masterCvText = `
Name: ${masterProfile.fullName}
Title: ${masterProfile.title}
Summary: ${masterProfile.summary}
Skills: ${masterProfile.skills.join(', ')}
Experience:
${masterProfile.experiences.map(e => `${e.role} at ${e.company}:\n${e.bullets.join('\n')}`).join('\n\n')}
`;

    const result = await tailorResumeWithAI({
      jobTitle: targetRole,
      company: targetCompany,
      jobDescription: jobText,
      masterCvText
    });

    setTailorResult(result);
    setIsProcessing(false);
    setCurrentStep(2); // Move to Step 2
  };

  const handleSaveApp = () => {
    const newApp: JobApplication = {
      id: `app_${Date.now()}`,
      company: targetCompany.trim() || 'Target Company',
      role: targetRole.trim() || 'Cybersecurity Specialist',
      location: 'Remote UK',
      salary_range: '£400 - £500/day (Contract)',
      job_type: 'Contract',
      status: 'applied',
      job_url: '',
      contact_person: '',
      contact_email: '',
      match_score: tailorResult?.matchScore || 92,
      tailored_cv_summary: tailorResult?.tailoredSummary || '',
      notes: `Applied with tailored CV. Top skills: ${tailorResult?.matchingKeywords.slice(0, 3).join(', ')}`,
      applied_date: new Date().toISOString().split('T')[0]
    };

    onSaveToPipeline(newApp);
    setIsSaved(true);
  };

  return (
    <div className="space-y-8 max-w-5xl mx-auto pb-16">
      
      {/* Welcome Header */}
      <div className="text-center space-y-3 bg-gradient-to-b from-slate-900 via-slate-900 to-slate-950 p-6 sm:p-8 rounded-3xl border border-slate-800 shadow-xl relative overflow-hidden">
        <div className="flex items-center justify-center space-x-2 flex-wrap gap-y-2">
          <div className="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-bold">
            <ShieldCheck className="w-4 h-4 text-emerald-400" />
            <span>Cybersecurity, InfoSec & MSP Mode Active</span>
          </div>
          <div className="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-sky-500/10 border border-sky-500/20 text-sky-400 text-xs font-bold">
            <Clock className="w-3.5 h-3.5" />
            <span>Scans for Contracts Every 45 Min</span>
          </div>
        </div>

        <h1 className="text-2xl sm:text-4xl font-extrabold text-white">
          Cyber & MSP Contracts in <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 via-teal-400 to-sky-400">3 Easy Clicks</span>
        </h1>
        <p className="text-slate-300 text-sm max-w-xl mx-auto">
          The system scans UK remote Cybersecurity, Information Security, and MSP overflow opportunities. Pick a role below or paste any job posting!
        </p>

        {/* Upload CV Action Callout */}
        <div className="pt-2">
          <button
            onClick={onOpenUploadCvModal}
            className="inline-flex items-center space-x-2 px-4 py-2 rounded-xl text-xs font-bold bg-slate-800 hover:bg-slate-700 text-emerald-300 border border-emerald-500/30 shadow-md transition-all"
          >
            <Upload className="w-4 h-4 text-emerald-400" />
            <span>Click here to Upload / Paste Your CV Text</span>
            <span className="px-1.5 py-0.2 rounded bg-emerald-500/20 text-emerald-400 text-[10px]">
              Profile: {masterProfile.title.slice(0, 30)}...
            </span>
          </button>
        </div>
      </div>

      {/* 3 Step Progress Bar */}
      <div className="grid grid-cols-3 gap-3 sm:gap-4">
        
        {/* Step 1 Tab Button */}
        <button
          onClick={() => setCurrentStep(1)}
          className={`p-4 rounded-2xl border text-left transition-all flex items-center space-x-3 ${
            currentStep === 1
              ? 'bg-sky-500/15 border-sky-500 text-sky-200 shadow-lg shadow-sky-500/10 ring-2 ring-sky-500/50'
              : 'bg-slate-900/60 border-slate-800 text-slate-400 hover:text-slate-200'
          }`}
        >
          <div className={`w-8 h-8 rounded-xl flex items-center justify-center font-black text-sm shrink-0 ${
            currentStep === 1 ? 'bg-sky-500 text-white' : 'bg-slate-800 text-slate-300'
          }`}>
            1
          </div>
          <div>
            <div className="text-xs font-bold text-white">Step 1</div>
            <div className="text-[11px] text-slate-400 hidden sm:block">Pick Cyber/MSP Role</div>
          </div>
        </button>

        {/* Step 2 Tab Button */}
        <button
          onClick={() => tailorResult && setCurrentStep(2)}
          disabled={!tailorResult}
          className={`p-4 rounded-2xl border text-left transition-all flex items-center space-x-3 ${
            currentStep === 2
              ? 'bg-emerald-500/15 border-emerald-500 text-emerald-200 shadow-lg shadow-emerald-500/10 ring-2 ring-emerald-500/50'
              : tailorResult
              ? 'bg-slate-900/60 border-slate-800 text-slate-300 hover:text-white'
              : 'bg-slate-900/20 border-slate-800/40 text-slate-600 cursor-not-allowed'
          }`}
        >
          <div className={`w-8 h-8 rounded-xl flex items-center justify-center font-black text-sm shrink-0 ${
            currentStep === 2 ? 'bg-emerald-500 text-white' : 'bg-slate-800 text-slate-400'
          }`}>
            2
          </div>
          <div>
            <div className="text-xs font-bold text-white">Step 2</div>
            <div className="text-[11px] text-slate-400 hidden sm:block">Get Tailored Security CV</div>
          </div>
        </button>

        {/* Step 3 Tab Button */}
        <button
          onClick={() => tailorResult && setCurrentStep(3)}
          disabled={!tailorResult}
          className={`p-4 rounded-2xl border text-left transition-all flex items-center space-x-3 ${
            currentStep === 3
              ? 'bg-indigo-500/15 border-indigo-500 text-indigo-200 shadow-lg shadow-indigo-500/10 ring-2 ring-indigo-500/50'
              : tailorResult
              ? 'bg-slate-900/60 border-slate-800 text-slate-300 hover:text-white'
              : 'bg-slate-900/20 border-slate-800/40 text-slate-600 cursor-not-allowed'
          }`}
        >
          <div className={`w-8 h-8 rounded-xl flex items-center justify-center font-black text-sm shrink-0 ${
            currentStep === 3 ? 'bg-indigo-500 text-white' : 'bg-slate-800 text-slate-400'
          }`}>
            3
          </div>
          <div>
            <div className="text-xs font-bold text-white">Step 3</div>
            <div className="text-[11px] text-slate-400 hidden sm:block">Copy Pitch & Apply</div>
          </div>
        </button>

      </div>

      {/* STEP 1: Auto-Found Jobs & Job Picker */}
      {currentStep === 1 && (
        <div className="rounded-3xl bg-slate-900 border border-slate-800 p-6 sm:p-8 space-y-6 shadow-xl animate-in fade-in">
          
          {/* Top Auto-Scanner Bar */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-slate-950 p-4 rounded-2xl border border-slate-800">
            <div className="flex items-center space-x-3">
              <div className="p-2.5 rounded-xl bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                <Bot className="w-5 h-5" />
              </div>
              <div>
                <div className="flex items-center space-x-2">
                  <h3 className="text-sm font-bold text-white">Live Cybersecurity & MSP Contract Opportunities</h3>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300">
                    £350 - £550/day
                  </span>
                </div>
                <p className="text-xs text-slate-400">Scanned from live UK InfoSec and MSP contract feeds</p>
              </div>
            </div>

            <button
              type="button"
              onClick={handleAutoSearchJobs}
              disabled={isAutoSearching}
              className="px-4 py-2 rounded-xl text-xs font-bold bg-slate-800 hover:bg-slate-700 text-emerald-400 border border-emerald-500/30 flex items-center justify-center space-x-2 transition-all"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${isAutoSearching ? 'animate-spin' : ''}`} />
              <span>{isAutoSearching ? 'Scanning Web...' : 'Refresh Cyber Roles'}</span>
            </button>
          </div>

          {/* Quick External Cyber Search Links */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 pt-1">
            <a
              href="https://www.jobserve.com/gb/en/Job-Search/?q=Cyber+Security+OR+InfoSec+OR+SOC&loc=Remote+UK&rad=0&contract=1"
              target="_blank"
              rel="noreferrer"
              className="p-3 rounded-xl bg-slate-950 border border-slate-800 hover:border-emerald-500 flex items-center justify-between text-xs font-semibold text-emerald-300 hover:text-white transition-all"
            >
              <span>🛡️ Jobserve UK Cyber Contracts</span>
              <ExternalLink className="w-3.5 h-3.5" />
            </a>

            <a
              href="https://www.google.com/maps/search/Managed+Service+Provider+MSP+London+Manchester+Leeds+Birmingham/"
              target="_blank"
              rel="noreferrer"
              className="p-3 rounded-xl bg-slate-950 border border-slate-800 hover:border-sky-500 flex items-center justify-between text-xs font-semibold text-sky-300 hover:text-white transition-all"
            >
              <span>🏢 Find UK MSP Companies</span>
              <ExternalLink className="w-3.5 h-3.5" />
            </a>

            <a
              href="https://cord.co/"
              target="_blank"
              rel="noreferrer"
              className="p-3 rounded-xl bg-slate-950 border border-slate-800 hover:border-amber-500 flex items-center justify-between text-xs font-semibold text-amber-300 hover:text-white transition-all"
            >
              <span>💬 Message CISOs on Cord.co</span>
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>

          {/* List of Auto-Discovered Roles (1-Click to Select) */}
          <div className="space-y-2 pt-2">
            <span className="text-xs font-bold text-slate-300 uppercase tracking-wider flex items-center space-x-1.5">
              <Flame className="w-4 h-4 text-amber-400" />
              <span>Click any role below to tailor your CV automatically:</span>
            </span>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {autoJobs.slice(0, 4).map((job) => (
                <div
                  key={job.id}
                  onClick={() => handleSelectDiscoveredJob(job)}
                  className={`p-4 rounded-2xl border cursor-pointer transition-all space-y-2 flex flex-col justify-between ${
                    selectedAutoJobId === job.id
                      ? 'bg-emerald-500/20 border-emerald-500 text-white ring-2 ring-emerald-500/40'
                      : 'bg-slate-950/80 border-slate-800/90 text-slate-300 hover:border-slate-700 hover:bg-slate-950'
                  }`}
                >
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-bold uppercase text-emerald-400 tracking-wider">
                        {job.company}
                      </span>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-300 border border-emerald-500/20">
                        {job.match_score || 94}% Fit
                      </span>
                    </div>
                    <h4 className="text-xs font-bold text-white">{job.role}</h4>
                    <p className="text-[11px] text-slate-400 line-clamp-2">{job.description}</p>
                  </div>

                  <div className="flex items-center justify-between pt-2 border-t border-slate-800/80 text-[11px]">
                    <span className="text-emerald-400 font-bold">{job.salary_range}</span>
                    <span className="text-sky-300 font-semibold text-[10px] flex items-center space-x-1">
                      <span>{selectedAutoJobId === job.id ? '✓ Selected' : 'Select Role ➔'}</span>
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Job Details Box */}
          <div className="space-y-4 pt-2 border-t border-slate-800">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold text-slate-300">Selected Target Role</label>
                <input
                  type="text"
                  value={targetRole}
                  onChange={(e) => setTargetRole(e.target.value)}
                  className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-sm text-white focus:border-emerald-500 outline-none"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-300">Company / MSP Name</label>
                <input
                  type="text"
                  value={targetCompany}
                  onChange={(e) => setTargetCompany(e.target.value)}
                  className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-sm text-white focus:border-emerald-500 outline-none"
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-bold text-slate-300">Job Description (or Security Requirements)</label>
              <textarea
                rows={4}
                value={jobText}
                onChange={(e) => setJobText(e.target.value)}
                className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-xs text-slate-200 focus:border-emerald-500 outline-none font-mono"
              />
            </div>

            {/* BIG ACTION BUTTON */}
            <button
              onClick={handleGenerateCV}
              disabled={isProcessing || !jobText.trim()}
              className="w-full py-4 px-6 rounded-2xl font-black text-base bg-gradient-to-r from-emerald-500 via-teal-500 to-sky-600 hover:from-emerald-400 hover:to-sky-500 text-white shadow-xl shadow-emerald-500/25 flex items-center justify-center space-x-3 transition-all disabled:opacity-50"
            >
              {isProcessing ? (
                <>
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  <span>Tailoring Your Cybersecurity CV...</span>
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5" />
                  <span>Make My CV Fit This Security Role (Step 2 ➔)</span>
                </>
              )}
            </button>
          </div>

        </div>
      )}

      {/* STEP 2: Get Your Ready CV */}
      {currentStep === 2 && tailorResult && (
        <div className="rounded-3xl bg-slate-900 border border-slate-800 p-6 sm:p-8 space-y-6 shadow-xl animate-in fade-in">
          
          <div className="flex items-center justify-between border-b border-slate-800 pb-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-2xl bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 flex items-center justify-center font-black">
                2
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Your Custom Security CV is Ready!</h2>
                <p className="text-xs text-slate-400">Tailored for <strong>{targetRole}</strong> at <strong>{targetCompany}</strong></p>
              </div>
            </div>

            <div className="px-3 py-1.5 rounded-xl bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 font-black text-sm">
              {tailorResult.matchScore}% ATS Match
            </div>
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <button
              onClick={() => onOpenPrintPreview(tailorResult.tailoredSummary, tailorResult.tailoredBullets)}
              className="py-4 px-6 rounded-2xl font-bold text-sm bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-white shadow-lg shadow-emerald-500/20 flex items-center justify-center space-x-2 transition-all"
            >
              <Printer className="w-5 h-5" />
              <span>Download / Print Ready PDF CV (Ctrl+P)</span>
            </button>

            <button
              onClick={() => setCurrentStep(3)}
              className="py-4 px-6 rounded-2xl font-bold text-sm bg-sky-500 hover:bg-sky-400 text-white shadow-lg shadow-sky-500/20 flex items-center justify-center space-x-2 transition-all"
            >
              <Send className="w-5 h-5" />
              <span>Next: Get Message for Hiring Lead (Step 3 ➔)</span>
            </button>
          </div>

          {/* Custom Summary Box */}
          <div className="p-5 rounded-2xl bg-slate-950 border border-slate-800 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-emerald-400 uppercase tracking-wider">Your Tailored Security Summary</span>
              <button
                onClick={() => handleCopy(tailorResult.tailoredSummary, 'sum')}
                className="text-xs text-slate-400 hover:text-white flex items-center space-x-1"
              >
                {copiedSection === 'sum' ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                <span>{copiedSection === 'sum' ? 'Copied!' : 'Copy'}</span>
              </button>
            </div>
            <p className="text-xs sm:text-sm text-slate-200 leading-relaxed">
              {tailorResult.tailoredSummary}
            </p>
          </div>

          {/* Custom Bullets Box */}
          <div className="p-5 rounded-2xl bg-slate-950 border border-slate-800 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-emerald-400 uppercase tracking-wider">High-Impact Security Accomplishment Points</span>
              <button
                onClick={() => handleCopy(tailorResult.tailoredBullets.map(b => `• ${b}`).join('\n'), 'all_bullets')}
                className="text-xs text-slate-400 hover:text-white flex items-center space-x-1"
              >
                {copiedSection === 'all_bullets' ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                <span>{copiedSection === 'all_bullets' ? 'Copied All!' : 'Copy All'}</span>
              </button>
            </div>
            <div className="space-y-2">
              {tailorResult.tailoredBullets.map((bullet, idx) => (
                <div key={idx} className="flex items-start space-x-2 text-xs text-slate-200">
                  <span className="text-emerald-400 font-bold">•</span>
                  <span>{bullet}</span>
                </div>
              ))}
            </div>
          </div>

        </div>
      )}

      {/* STEP 3: Copy Message & Apply */}
      {currentStep === 3 && tailorResult && (
        <div className="rounded-3xl bg-slate-900 border border-slate-800 p-6 sm:p-8 space-y-6 shadow-xl animate-in fade-in">
          
          <div className="flex items-center space-x-3 border-b border-slate-800 pb-4">
            <div className="w-10 h-10 rounded-2xl bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 flex items-center justify-center font-black">
              3
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Copy Your Pitch Message & Send</h2>
              <p className="text-xs text-slate-400">Send directly to the MSP Technical Director or Security Lead on LinkedIn / Email</p>
            </div>
          </div>

          {/* Quick Copy Message Box */}
          <div className="p-6 rounded-2xl bg-slate-950 border border-slate-800 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-indigo-400 uppercase tracking-wider">
                📩 Direct Security Pitch (Ready to Send)
              </span>
              <button
                onClick={() => handleCopy(`Subject: ${tailorResult.coldEmailOutreach.subject}\n\n${tailorResult.coldEmailOutreach.body}`, 'full_email')}
                className="px-4 py-2 rounded-xl text-xs font-bold bg-indigo-600 hover:bg-indigo-500 text-white shadow-md flex items-center space-x-1.5 transition-all"
              >
                {copiedSection === 'full_email' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                <span>{copiedSection === 'full_email' ? 'Copied Message!' : '1-Click Copy Pitch'}</span>
              </button>
            </div>

            <div className="text-xs text-slate-300 font-mono bg-slate-900 p-4 rounded-xl border border-slate-800/80 whitespace-pre-wrap leading-relaxed">
              <strong>Subject:</strong> {tailorResult.coldEmailOutreach.subject}
              {"\n\n"}
              {tailorResult.coldEmailOutreach.body}
            </div>
          </div>

          {/* Save to Tracker */}
          <div className="p-5 rounded-2xl bg-gradient-to-r from-slate-950 to-indigo-950/40 border border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div>
              <h4 className="text-sm font-bold text-white">Save this to your personal tracker?</h4>
              <p className="text-xs text-slate-400">Keep track of where you applied so you remember to follow up.</p>
            </div>

            <button
              onClick={handleSaveApp}
              disabled={isSaved}
              className={`px-5 py-2.5 rounded-xl text-xs font-bold flex items-center space-x-2 transition-all ${
                isSaved
                  ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
                  : 'bg-emerald-500 hover:bg-emerald-400 text-white shadow-lg shadow-emerald-500/25'
              }`}
            >
              {isSaved ? <Check className="w-4 h-4" /> : <PlusCircle className="w-4 h-4" />}
              <span>{isSaved ? 'Saved in Application Pipeline!' : 'Save to Job Pipeline'}</span>
            </button>
          </div>

          {/* Start New Job */}
          <div className="text-center pt-2">
            <button
              onClick={() => {
                setCurrentStep(1);
                setJobText('');
                setTargetCompany('');
                setTargetRole('');
              }}
              className="text-xs text-slate-400 hover:text-white underline"
            >
              Tailor another role ➔
            </button>
          </div>

        </div>
      )}

    </div>
  );
};
